/**
 * Hand-drawn-feeling SVG chart primitives — no chart library.
 * Ink-on-paper: stroke-only, single-hue, 1.4–1.6px stroke,
 * red used sparingly as accent (last point, highlights).
 */

const INK = '#1F140C';
const RED = '#E30613';

/* ----- Sparkline ----- */
export function Sparkline({
    values,
    width = 92,
    height = 28,
    accent = false,
}: {
    values: number[];
    width?: number;
    height?: number;
    accent?: boolean;
}) {
    if (!values.length) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const stepX = width / (values.length - 1);
    const pts = values.map((v, i) => {
        const x = i * stepX;
        const y = height - ((v - min) / range) * (height - 4) - 2;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    const lastX = (values.length - 1) * stepX;
    const lastY = height - ((values[values.length - 1] - min) / range) * (height - 4) - 2;
    const stroke = accent ? RED : INK;
    return (
        <svg width={width} height={height} style={{ overflow: 'visible', display: 'block' }}>
            <polyline
                points={pts.join(' ')}
                fill="none"
                stroke={stroke}
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={accent ? 1 : 0.7}
            />
            <circle cx={lastX} cy={lastY} r="2.5" fill={stroke} />
        </svg>
    );
}

/* ----- Line chart with axis labels (cocoa price) ----- */
export function LineChart({
    values,
    labels,
    width = 320,
    height = 130,
    annotations,
}: {
    values: number[];
    labels: string[];
    width?: number;
    height?: number;
    annotations?: { index: number; text: string }[];
}) {
    const padX = 6;
    const padY = 14;
    const innerW = width - padX * 2;
    const innerH = height - padY * 2;

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const stepX = innerW / (values.length - 1);

    const pts = values.map((v, i) => {
        const x = padX + i * stepX;
        const y = padY + innerH - ((v - min) / range) * innerH;
        return [x, y] as [number, number];
    });

    const path = pts.map((p, i) => (i === 0 ? `M${p[0].toFixed(1)},${p[1].toFixed(1)}` : `L${p[0].toFixed(1)},${p[1].toFixed(1)}`)).join(' ');
    const last = pts[pts.length - 1];

    return (
        <svg width="100%" viewBox={`0 0 ${width} ${height + 16}`} style={{ display: 'block', overflow: 'visible' }}>
            {/* baseline */}
            <line x1={padX} x2={width - padX} y1={height - padY} y2={height - padY} stroke={INK} strokeOpacity="0.18" strokeWidth="1" />
            {/* main line */}
            <path d={path} fill="none" stroke={INK} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            {/* last point */}
            <circle cx={last[0]} cy={last[1]} r="3.5" fill={RED} />
            {/* annotations */}
            {annotations?.map((a, idx) => {
                const p = pts[a.index];
                if (!p) return null;
                const labelX = p[0];
                const labelY = p[1] - 14;
                return (
                    <g key={idx}>
                        <circle cx={p[0]} cy={p[1]} r="2.2" fill={INK} opacity="0.7" />
                        <line x1={p[0]} x2={p[0]} y1={p[1] - 4} y2={labelY + 6} stroke={INK} strokeOpacity="0.5" strokeDasharray="2,2" strokeWidth="1" />
                        <text
                            x={labelX}
                            y={labelY}
                            textAnchor="middle"
                            fontFamily="JetBrains Mono, monospace"
                            fontSize="8"
                            fill={INK}
                            opacity="0.7"
                            letterSpacing="0.05em"
                        >
                            {a.text.toUpperCase()}
                        </text>
                    </g>
                );
            })}
            {/* x-axis labels */}
            {labels.map((lab, i) => {
                if (i % Math.ceil(labels.length / 6) !== 0 && i !== labels.length - 1) return null;
                const x = padX + i * stepX;
                return (
                    <text
                        key={i}
                        x={x}
                        y={height + 8}
                        textAnchor="middle"
                        fontFamily="JetBrains Mono, monospace"
                        fontSize="8"
                        fill={INK}
                        opacity="0.55"
                        letterSpacing="0.1em"
                    >
                        {lab.toUpperCase()}
                    </text>
                );
            })}
        </svg>
    );
}

/* ----- Bar row (used in horizontal-bar list) ----- */
export function BarRow({
    label,
    value,
    delta,
    pct,
    accent = false,
}: {
    label: string;
    value: string;
    delta?: string;
    pct: number;        /* 0–100 */
    accent?: boolean;
}) {
    return (
        <div className="bar-row">
            <div className="bar-label">{label}</div>
            <div className="bar-track">
                <div
                    className="bar-fill"
                    style={{
                        width: `${Math.max(2, pct)}%`,
                        background: accent ? 'var(--brynild-red)' : 'var(--ink-cocoa)',
                        opacity: accent ? 1 : 0.85,
                    }}
                />
            </div>
            <div className="bar-value">
                <span className="bar-value-num">{value}</span>
                {delta && (
                    <span className={`bar-delta ${delta.startsWith('+') ? 'pos' : delta.startsWith('-') ? 'neg' : ''}`}>
                        {delta}
                    </span>
                )}
            </div>
        </div>
    );
}

/* ----- 7×4 day-cell heatmap (28 days) ----- */
type Cell = 'ok' | 'warn' | 'fail' | 'future';

export function DayGrid({ days, dayLabels }: { days: Cell[]; dayLabels?: string[] }) {
    const cellSize = 22;
    const gap = 4;
    const cols = 7;
    const rows = Math.ceil(days.length / cols);
    const w = cols * cellSize + (cols - 1) * gap;
    const h = rows * cellSize + (rows - 1) * gap + (dayLabels ? 18 : 0);

    return (
        <svg width={w} height={h} style={{ display: 'block' }}>
            {dayLabels?.map((d, i) => (
                <text
                    key={d + i}
                    x={i * (cellSize + gap) + cellSize / 2}
                    y={10}
                    textAnchor="middle"
                    fontFamily="JetBrains Mono, monospace"
                    fontSize="8"
                    fill={INK}
                    opacity="0.55"
                    letterSpacing="0.1em"
                >
                    {d.toUpperCase()}
                </text>
            ))}
            {days.map((c, i) => {
                const col = i % cols;
                const row = Math.floor(i / cols);
                const x = col * (cellSize + gap);
                const y = (dayLabels ? 18 : 0) + row * (cellSize + gap);
                let fill = 'rgba(31,20,12,0.06)';
                let stroke = 'rgba(31,20,12,0.18)';
                if (c === 'ok') {
                    fill = INK;
                    stroke = INK;
                } else if (c === 'warn') {
                    fill = 'var(--brynild-gold)';
                    stroke = 'var(--brynild-gold)';
                } else if (c === 'fail') {
                    fill = RED;
                    stroke = RED;
                }
                return (
                    <rect
                        key={i}
                        x={x}
                        y={y}
                        width={cellSize}
                        height={cellSize}
                        rx="1"
                        fill={fill}
                        stroke={stroke}
                        strokeWidth="1"
                        opacity={c === 'ok' ? 0.85 : 1}
                    />
                );
            })}
        </svg>
    );
}
